import React, { useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  GlobalOutlined,
  CodeOutlined,
  WarningOutlined,
  CloudServerOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { App } from "../types/api";
import { Template } from "../types/api";
import { AppStatus, DeploymentStatus } from "../types/enums";
import { usePersistedState } from "../hooks/usePersistedState";

const { Title, Text } = Typography;
const { Option } = Select;

/** Ключ, під яким тримаємо останнє пошукове слово між переходами на екран апки і назад. */
const APPS_SEARCH_STORAGE_KEY = "apps:searchQuery";

export interface AppsTableProps {
  apps: App[];
  loading: boolean;
  templates: Template[];
  selectedRowKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  onCreate: () => void;
  onEdit: (app: App) => void;
  onDeploy: (appId: string) => void;
  onDelete: (appId: string) => void;
  onViewWorkerCode: (appId: string) => void;
  onDeployBulk: () => void;
  onDeleteBulk: () => void;
  deployBulkPending?: boolean;
  deleteBulkPending?: boolean;
  /** id апки, для якої зараз показується/завантажується код воркера */
  workerCodeAppId?: string | null;
  workerCodeLoading?: boolean;
}

export const AppsTable: React.FC<AppsTableProps> = ({
  apps,
  loading,
  templates,
  selectedRowKeys,
  onSelectionChange,
  onCreate,
  onEdit,
  onDeploy,
  onDelete,
  onViewWorkerCode,
  onDeployBulk,
  onDeleteBulk,
  deployBulkPending = false,
  deleteBulkPending = false,
  workerCodeAppId = null,
  workerCodeLoading = false,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = usePersistedState<string>(
    APPS_SEARCH_STORAGE_KEY,
    ""
  );

  const filteredApps = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(
      (a) =>
        a.name?.toLowerCase().includes(q) ||
        a.customId?.toLowerCase().includes(q) ||
        a.domain?.toLowerCase().includes(q)
    );
  }, [apps, searchQuery]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => onSelectionChange(keys as string[]),
  };

  const columns: ColumnsType<App> = [
    {
      title: t("apps.name"),
      dataIndex: "name",
      key: "name",
      render: (text: string, record: App) => (
        <Space direction="vertical" size="small">
          <Text strong>{text}</Text>
          <Space>
            {record.deploymentStatus === DeploymentStatus.DEPLOYING ||
            record.deploymentStatus?.toUpperCase() === DeploymentStatus.DEPLOYING ? (
              <Tag color="orange">{t("apps.deploying")}</Tag>
            ) : record.deploymentStatus === DeploymentStatus.DEPLOYED ||
              record.deploymentStatus?.toUpperCase() === DeploymentStatus.DEPLOYED ? (
              <Tag color="green">{t("apps.deployed")}</Tag>
            ) : record.deploymentStatus === DeploymentStatus.INACTIVE ||
              record.deploymentStatus?.toUpperCase() === DeploymentStatus.INACTIVE ? (
              <Tag color="default">{t("apps.inactive")}</Tag>
            ) : record.status === AppStatus.ACTIVE ||
              record.status?.toUpperCase() === AppStatus.ACTIVE ? (
              <Tag color="green">{t("apps.active")}</Tag>
            ) : (
              <Tag color="default">{t("apps.inactive")}</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: "Custom ID",
      dataIndex: "customId",
      key: "customId",
      width: 180,
      render: (text: string, record: App) => (
        <Text code>
          {(text && String(text)) ||
            (record.customId ? String(record.customId) : record.id)}
        </Text>
      ),
    },
    {
      title: t("apps.domain"),
      dataIndex: "domain",
      key: "domain",
      render: (text: string) => (
        <Space>
          <GlobalOutlined />
          <Text code>{text}</Text>
        </Space>
      ),
    },
    {
      title: t("apps.template"),
      dataIndex: "templateId",
      key: "templateId",
      render: (_: string, record: App & { template?: { name: string; version?: string }; templateVersionInfo?: { isOutdated?: boolean; latestBuildNumber?: number; currentBuildNumber?: number } }) => {
        const template = record.template;
        const isOutdated = record.templateVersionInfo?.isOutdated;
        return (
          <Space>
            <CodeOutlined />
            <Text>
              {template
                ? `${template.name}${template.version ? ` v${template.version}` : ""}`
                : t("apps.unknown")}
            </Text>
            {isOutdated && (
              <Tooltip
                title={`Доступна нова версія (${t("templates.buildNumber")} #${record.templateVersionInfo?.latestBuildNumber}). Поточна версія: ${t("templates.buildNumber")} #${record.templateVersionInfo?.currentBuildNumber}`}
              >
                <WarningOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: t("apps.lastUpdate"),
      dataIndex: "updatedAt",
      key: "updatedAt",
    },
    {
      title: t("apps.actions"),
      key: "actions",
      render: (_: unknown, record: App) => (
        <Space>
          <Tooltip title={t("apps.edit")}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title={t("apps.deploy")}>
            <Button
              type="text"
              icon={<PlayCircleOutlined />}
              onClick={() => onDeploy(record.id)}
              loading={loading}
            />
          </Tooltip>
          {(record.deploymentStatus === DeploymentStatus.DEPLOYED ||
            record.deploymentStatus?.toUpperCase() === DeploymentStatus.DEPLOYED) && (
            <Tooltip title={t("apps.workerCode")}>
              <Button
                type="text"
                icon={<CodeOutlined />}
                onClick={() => onViewWorkerCode(record.id)}
                loading={workerCodeAppId === record.id && workerCodeLoading}
              />
            </Tooltip>
          )}
          <Tooltip title={t("apps.delete")}>
            <Popconfirm
              title={t("apps.confirmDelete")}
              onConfirm={() => onDelete(record.id)}
              okText={t("common.yes")}
              cancelText={t("common.no")}
            >
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Title style={{ margin: 0 }} level={2}>
            {t("apps.title")}
          </Title>
          <Input.Search
            placeholder="Пошук по customId, domain and name"
            value={searchQuery}
            onSearch={(v) => setSearchQuery(v || "")}
            onChange={(e) => setSearchQuery(e.target.value || "")}
            style={{ width: 320 }}
            allowClear
          />
          <Select
            placeholder="Шаблон (вибрати всі апки шаблону)"
            allowClear
            style={{ width: 280 }}
            onChange={(templateId: string | undefined) => {
              onSelectionChange(
                templateId
                  ? filteredApps
                      .filter((a) => a.templateId === templateId)
                      .map((a) => a.id)
                  : []
              );
            }}
          >
            {templates.map((template) => (
              <Option key={template.id} value={template.id}>
                {template.name}
                {template.version ? ` v${template.version}` : ""}
              </Option>
            ))}
          </Select>
        </div>

        <Space>
          {selectedRowKeys.length > 0 && (
            <>
              <Button
                icon={<CloudServerOutlined />}
                onClick={onDeployBulk}
                loading={deployBulkPending}
              >
                {t("apps.bulkDeployButton", { count: selectedRowKeys.length })}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={onDeleteBulk}
                loading={deleteBulkPending}
              >
                {t("apps.bulkDeleteButton", { count: selectedRowKeys.length })}
              </Button>
            </>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            {t("apps.create")}
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          dataSource={filteredApps}
          columns={columns}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{ pageSize: 10 }}
          loading={loading}
        />
      </Card>
    </div>
  );
};
